import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, output, signal, untracked, viewChild } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import { CONFIRMER } from '@shared/components/dialogs/confirm/types';
import {
  HdGateGuessingGameService,
  HdGateGuessingHistoryEvent,
  HdGateGuessingMemoryAnswerResult,
  HdGateGuessingMemoryField,
  HdGateGuessingScoreLevel,
} from '../hd-gate-guessing-game.service';
import { HdGateInfo, HdGateInfoService } from '../../hd-gate-info.service';
import { HdGateInfoCheatSheetComponent } from '../../gate-info/variants/hd-gate-info-cheat-sheet.component';
import { HdGateInfoContext } from '../../gate-info/hd-gate-info-context.model';
import { HdGameFeedbackAudioService } from '../../hd-game-feedback-audio.service';
import { HdCardFeedbackComponent, HdCardFeedbackKind } from '../../card-feedback/hd-card-feedback.component';

type HdGateGuessingHistoryGroupPosition = 'single' | 'start' | 'middle' | 'end';

/**
 * Eén stukje hint-info dat na opeenvolgende klikken op de hint-knop wordt
 * onthuld. `kind` bepaalt enkel de iconografie/grouping; `categoryLabel` is
 * de naam van de informatie-categorie (bv. "Center", "Hoofdcircuit") en
 * `value` de feitelijke onthulde waarde.
 */
interface HdGateGuessingHintSlot {
  readonly kind: 'center' | 'mainCircuit' | 'extraNote' | 'physicalNote' | 'gateNumber';
  readonly categoryLabel: string;
  /** HTML-renderbare waarde: simpele `**bold**` is omgezet naar `<strong>`. */
  readonly valueHtml: string;
}

interface HdGateGuessingHistoryRow {
  readonly key: string;
  readonly kind: HdGateGuessingHistoryEvent['kind'];
  readonly label: string;
  readonly icon: string;
  readonly iconClass: string;
  readonly pointsLabel: string;
  readonly pointsClass: string;
  readonly elapsedLabel: string | null;
  readonly elapsedIsSlow: boolean;
  readonly groupPosition: HdGateGuessingHistoryGroupPosition;
  /** Poort-nummer voor rijen die aan een specifieke poort hangen, anders `null`. */
  readonly gate: number | null;
}

interface HdGateGuessingSlowestGate {
  readonly gate: number;
  readonly elapsedLabel: string;
}

const SLOW_OUTLIER_MIN_SAMPLE_SIZE = 5;
const SLOW_OUTLIER_IQR_MULTIPLIER = 1.5;
const TOP_SLOWEST_GATES_COUNT = 3;
const MEMORY_CELEBRATION_DURATION_MS = 1500;
const MEMORY_WRONG_ANIMATION_DURATION_MS = 1500;

const SCORE_LEVEL_TITLE_KEYS: Readonly<Record<HdGateGuessingScoreLevel, string>> = {
  1: 'humanDesign.games.gateGuessing.scoreLevel1',
  2: 'humanDesign.games.gateGuessing.scoreLevel2',
  3: 'humanDesign.games.gateGuessing.scoreLevel3',
  4: 'humanDesign.games.gateGuessing.scoreLevel4',
  5: 'humanDesign.games.gateGuessing.scoreLevel5',
};

@Component({
  selector: 'hd-gate-guessing-game-panel',
  imports: [SharedModule, HdGateInfoCheatSheetComponent, HdCardFeedbackComponent],
  templateUrl: './hd-gate-guessing-game-panel.component.html',
  styleUrl: './hd-gate-guessing-game-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdGateGuessingGamePanelComponent {
  readonly restartRequested = output<void>();

  protected readonly game = inject(HdGateGuessingGameService);
  private readonly confirmer = inject(CONFIRMER);
  private readonly gateInfoService = inject(HdGateInfoService);
  private readonly feedbackAudio = inject(HdGameFeedbackAudioService);
  private readonly transloco = inject(TranslocoService);

  /**
   * Vertaalt een sleutel in de **bevroren inhoudstaal** van het lopende spel
   * (`game.currentLang()`, afgeleid van de content-taal-override uit het
   * controls-paneel of de app-taal). Zo schakelt het hele paneel mee wanneer
   * de speler het spel in een andere taal start.
   */
  protected t(key: string, params?: Record<string, unknown>): string {
    return this.transloco.translate(key, params, this.game.currentLang());
  }

  /**
   * Referentie op het poortnummer-invoerveld in `memory-poort`-modus zodat we
   * de focus automatisch kunnen verleggen na een game-start, gate-wissel of
   * spel-actie (hint, skip, foutief antwoord). Dat laat de speler vlot via
   * het toetsenbord werken zonder telkens manueel het veld te selecteren.
   */
  private readonly memoryGateInputEl =
    viewChild<ElementRef<HTMLInputElement>>('memoryGateInputEl');

  /**
   * Referentie op het keynote1-invoerveld in `memory-keynote`-modus zodat we
   * de focus automatisch kunnen verleggen na een game-start of gate-wissel.
   * Spiegelt {@link memoryGateInputEl} voor de keynote-variant.
   */
  private readonly memoryKeynote1InputEl =
    viewChild<ElementRef<HTMLInputElement>>('memoryKeynote1InputEl');

  protected readonly currentGate = this.game.currentGate;
  protected readonly canRequestHint = this.game.canRequestHint;
  protected readonly canSkip = this.game.canSkip;
  protected readonly isFinished = this.game.isFinished;
  protected readonly isPerfectGame = this.game.isPerfectGame;
  protected readonly scoreLevel = this.game.scoreLevel;
  protected readonly maxPossibleScore = this.game.maxPossibleScore;
  protected readonly maxPossibleScoreWithTimeBonus = this.game.maxPossibleScoreWithTimeBonus;
  protected readonly timeBonus = this.game.timeBonus;
  protected readonly timeBonusLabel = this.game.timeBonusLabel;
  protected readonly timerVisible = this.game.timerVisible;

  protected readonly currentQuestionMode = this.game.currentQuestionMode;
  protected readonly isMemoryMode = this.game.isMemoryMode;
  protected readonly mode = this.game.mode;
  protected readonly memoryValidations = this.game.currentMemoryValidations;

  /**
   * Per-veld vlag of het paneel een 'Valideer'-knop voor het keynote-veld mag
   * tonen. Volgt `canValidateMemoryField` op de service: zodra één veld
   * gevalideerd is, verdwijnen beide knoppen en moet de speler submitten.
   */
  protected readonly canValidateMemoryKeynote1 = computed(
    () => this.game.canValidateMemoryField('keynote1'),
  );
  protected readonly canValidateMemoryKeynote2 = computed(
    () => this.game.canValidateMemoryField('keynote2'),
  );

  /**
   * Autocomplete-suggestie per keynote-veld in `memory-keynote`-modus. Levert
   * de canonieke spelling van het doelwit zodra de getypte tekst dichtbij
   * genoeg ligt (woord-overlap of Levenshtein-similariteit). Wordt onderdrukt
   * zodra het veld reeds gevalideerd is. Spiegelt het mechanisme in de
   * channel-game (`memoryNameAutocompleteSuggestion` / `memoryDescription...`).
   */
  protected readonly memoryKeynote1AutocompleteSuggestion = computed<string | null>(() => {
    if (this.mode() !== 'memory-keynote' || this.memoryValidations().keynote1) {
      return null;
    }
    return this.game.getMemoryAutocompleteSuggestion('keynote1', this.memoryKeynote1Input());
  });

  protected readonly memoryKeynote2AutocompleteSuggestion = computed<string | null>(() => {
    if (this.mode() !== 'memory-keynote' || this.memoryValidations().keynote2) {
      return null;
    }
    return this.game.getMemoryAutocompleteSuggestion('keynote2', this.memoryKeynote2Input());
  });

  /**
   * Memory-modus invoervelden. We bewaren ze als signals zodat het paneel
   * de error-stijl per veld kan resetten zodra de speler begint te typen
   * en de hint-onthulling zijn nieuwe waardes rechtstreeks kan toepassen.
   */
  protected readonly memoryGateInput = signal('');
  protected readonly memoryKeynote1Input = signal('');
  protected readonly memoryKeynote2Input = signal('');
  protected readonly memoryGateWrong = signal(false);
  protected readonly memoryKeynote1Wrong = signal(false);
  protected readonly memoryKeynote2Wrong = signal(false);

  /**
   * Snapshot van de poort die zojuist correct werd beantwoord in een memory-
   * modus. Zolang dit niet `null` is, wordt de cheat-sheet bevroren op deze
   * poort en tonen we het ontbrekende deel (bv. het poortnummer in
   * `memory-poort`) plus een groene rand + checkmark-overlay als celebratie.
   * Wordt na {@link MEMORY_CELEBRATION_DURATION_MS} terug op `null` gezet.
   */
  protected readonly celebratingGate = signal<number | null>(null);
  private celebrationTimerId: ReturnType<typeof setTimeout> | null = null;

  /**
   * Spiegelt {@link celebratingGate} maar voor een fout antwoord: zolang dit
   * `true` is tonen we een rode cirkel met wit kruisje als overlay op de
   * cheat-sheet. De kaart zelf verandert niet (de poort blijft dezelfde),
   * enkel de overlay licht kort op om de fout visueel te benadrukken.
   */
  protected readonly wrongAnimating = signal(false);
  private wrongAnimationTimerId: ReturnType<typeof setTimeout> | null = null;

  /** Feedback-soort voor de gedeelde kaart-ring: celebratie wint van een lopende fout-animatie. */
  protected readonly memoryFeedback = computed<HdCardFeedbackKind | null>(() =>
    this.celebratingGate() != null ? 'correct' : this.wrongAnimating() ? 'wrong' : null,
  );

  /**
   * Cheat-sheet-context voor de huidige poort in memory-modus. We tonen de
   * kaart zonder planeet-context (geen activations) — het gaat enkel om de
   * inhoud van de poort zelf.
   */
  protected readonly memoryCheatSheetContext = computed<HdGateInfoContext | null>(() => {
    const gate = this.celebratingGate() ?? this.currentGate();
    if (gate == null || !this.isMemoryMode()) {
      return null;
    }
    return { gate };
  });

  /**
   * Tijdens een lopende celebratie (correct antwoord) verbergen we het
   * ontbrekende deel niet langer: in `memory-poort` betekent dit dat het
   * poortnummer plots zichtbaar wordt op de kaart als verduidelijking van
   * het juiste antwoord, vóór de kaart fadet naar de volgende vraag.
   */
  protected readonly memoryPoortHidesGateNumber = computed(
    () => this.mode() === 'memory-poort' && this.celebratingGate() == null,
  );

  /**
   * Verbergt initieel de center-kleur, center-info, property-iconen en het
   * circuit-kleurblokje op de cheat-sheet in `memory-poort`-modus. Wordt
   * onthuld zodra de speler de (eenmalige) hint vraagt — dat verhoogt
   * `hintLevelForCurrentGate` van 0 naar 1. Tijdens een lopende celebratie
   * tonen we de kleur sowieso (samen met het poortnummer), consistent met
   * de bestaande reveal-bij-correct logica.
   */
  protected readonly memoryPoortHidesColors = computed(
    () =>
      this.mode() === 'memory-poort'
      && this.celebratingGate() == null
      && this.game.hintLevelForCurrentGate() === 0,
  );
  protected readonly memoryKeynoteHidesKeynotes = computed(
    () => this.mode() === 'memory-keynote' && this.celebratingGate() == null,
  );
  protected readonly memoryPoortHidesFooter = computed(
    () => this.mode() === 'memory-poort' && this.celebratingGate() == null,
  );

  /** Of de huidige poort een tweede keynote heeft (bv. poort 49 niet). */
  protected readonly currentGateHasKeynote2 = computed(() => {
    const gate = this.currentGate();
    if (gate == null) {
      return false;
    }
    return this.gateInfoService.gateInfoIn(gate, this.game.currentLang()).translations.keynote2 !== '';
  });

  /**
   * Keynotes voor de huidige poort (enkel relevant in `keynotes`-vraagmodus).
   * Geeft `null` als er geen actieve poort is of als de vraagmodus `numbers` is.
   */
  protected readonly currentGateKeynotes = computed<{ keynote1: string; keynote2: string } | null>(() => {
    const gate = this.currentGate();
    if (gate == null || this.currentQuestionMode() !== 'keynotes') {
      return null;
    }
    const { translations } = this.gateInfoService.gateInfoIn(gate, this.game.currentLang());
    return { keynote1: translations.keynote1, keynote2: translations.keynote2 };
  });

  /**
   * Reset de memory-invoer en error-vlaggen telkens de huidige poort verandert
   * (start, skip, of correct antwoord). Voorkomt dat een vorige fout-stijl of
   * een hint-onthulling van de vorige poort blijft hangen op de volgende.
   */
  private readonly _resetMemoryInputsOnGateChangeEffect = effect(() => {
    this.currentGate();
    untracked(() => this.clearMemoryInputs());
  });

  /**
   * Zet automatisch de focus op het poortnummer-invoerveld zodra een nieuwe
   * poort in `memory-poort`-modus actief wordt (game-start, skip of een
   * correct antwoord dat de volgende poort triggert). Zo kan de speler
   * onmiddellijk verder met het toetsenbord zonder telkens manueel het veld
   * te moeten aanklikken.
   */
  private readonly _focusMemoryGateInputOnGateChangeEffect = effect(() => {
    const gate = this.currentGate();
    const mode = this.mode();
    if (gate == null || mode !== 'memory-poort') {
      return;
    }
    untracked(() => this.focusMemoryGateInput());
  });

  /**
   * Spiegelt {@link _focusMemoryGateInputOnGateChangeEffect} voor de
   * `memory-keynote`-modus: zet de cursor op keynote1 zodra het spel start
   * of een nieuwe poort aan de beurt komt.
   */
  private readonly _focusMemoryKeynote1InputOnGateChangeEffect = effect(() => {
    const gate = this.currentGate();
    const mode = this.mode();
    if (gate == null || mode !== 'memory-keynote') {
      return;
    }
    untracked(() => this.focusMemoryKeynote1Input());
  });

  protected readonly currentGateWrongAttemptIndices = computed(() =>
    indicesArray(this.game.wrongAttemptsForCurrentGate()),
  );

  /**
   * Volledige geordende lijst van hint-slots voor de huidige poort. Volgorde:
   * center → hoofdcircuit → extraNotes (in de bij eerste-zicht vastgelegde
   * willekeurige volgorde) → physicalNotes → poortnummer (enkel relevant bij
   * `keynotes`-vraagweergave). Slots zonder beschikbare data worden gefilterd.
   */
  protected readonly hintSlots = computed<readonly HdGateGuessingHintSlot[]>(() => {
    const gate = this.currentGate();
    if (gate == null) {
      return [];
    }
    const lang = this.game.currentLang();
    const info = this.gateInfoService.gateInfoIn(gate, lang);
    const order = this.game.hintExtraNotesOrderForCurrentGate();
    const questionMode = this.currentQuestionMode();
    return buildHintSlots(gate, info, order, questionMode, this.gateInfoService, lang);
  });

  protected readonly revealedHints = computed<readonly HdGateGuessingHintSlot[]>(() =>
    this.hintSlots().slice(0, this.game.hintLevelForCurrentGate()),
  );

  /**
   * Effect dat de hint-capaciteit (max niveau + willekeurige volgorde van
   * extraNotes) één keer per poort vastlegt. Wacht tot de translations
   * geladen zijn (sentinel `keynote1`) zodat de berekende max-waarde correct
   * is. De service is idempotent zodat heractivatie bij taalwissel geen
   * effect heeft.
   */
  private readonly _hintCapacityInitEffect = effect(() => {
    const gate = this.currentGate();
    if (gate == null) {
      return;
    }
    const info = this.gateInfoService.gateInfoIn(gate, this.game.currentLang());
    if (info.translations.keynote1 === '') {
      return;
    }
    const questionMode = untracked(() => this.currentQuestionMode());
    const extraNotesOrder = randomPermutation(info.translations.extraNotes.length);
    const maxLevel = computeHintMaxLevel(info, questionMode);
    untracked(() => this.game.setHintCapacityForCurrentGate(maxLevel, extraNotesOrder));
  });

  /**
   * Drempel waarboven een poort als merkbaar trager dan de rest geldt
   * (Tukey-outlier op basis van Q3 + 1.5·IQR over alle voltooide tijden).
   * Geeft `null` wanneer er onvoldoende data is of de spreiding nihil is.
   */
  protected readonly slowGateThresholdMs = computed<number | null>(() =>
    computeSlowGateThresholdMs([...this.game.completedGateTimings().values()]),
  );

  protected readonly historyRows = computed<readonly HdGateGuessingHistoryRow[]>(() => {
    const events = this.game.events();
    const slowThreshold = this.slowGateThresholdMs();
    return events.map((event, index) =>
      buildHistoryRow(
        events,
        index,
        slowThreshold,
        (gate, kind) => this.historyGateLabel(gate, kind),
        (key, params) => this.t(key, params),
      ),
    );
  });

  /**
   * Vrije zoekterm waarmee de speler de history-lijst kan filteren op
   * poortnummer of op een fragment van de keynote-tekst. Wordt automatisch
   * terug leeg gemaakt wanneer de history leegloopt (bv. bij een herstart).
   */
  protected readonly historySearch = signal('');

  protected readonly filteredHistoryRows = computed<readonly HdGateGuessingHistoryRow[]>(() => {
    const rows = this.historyRows();
    const term = this.historySearch().trim().toLowerCase();
    if (term === '') {
      return rows;
    }
    const hintSlotsByGate = this.historyHintSlotsByGate();
    return rows.filter(row => {
      if (row.gate != null && row.gate.toString().includes(term)) {
        return true;
      }
      if (row.label.toLowerCase().includes(term)) {
        return true;
      }
      if (row.gate != null && this.hintSlotsMatchTerm(hintSlotsByGate.get(row.gate), term)) {
        return true;
      }
      return false;
    });
  });

  private hintSlotsMatchTerm(
    slots: readonly HdGateGuessingHintSlot[] | undefined,
    term: string,
  ): boolean {
    if (!slots) {
      return false;
    }
    for (const slot of slots) {
      const categoryText = this.t(slot.categoryLabel).toLowerCase();
      if (categoryText.includes(term)) {
        return true;
      }
      const valueText = stripHtmlTags(slot.valueHtml).toLowerCase();
      if (valueText.includes(term)) {
        return true;
      }
    }
    return false;
  }

  private readonly _resetHistorySearchWhenHistoryEmptiesEffect = effect(() => {
    if (this.historyRows().length === 0 && this.historySearch() !== '') {
      untracked(() => this.historySearch.set(''));
    }
  });

  protected onHistorySearchInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.historySearch.set(target?.value ?? '');
  }

  /**
   * Bouwt het label voor een poort-gebonden history-rij.
   *
   * - `correct`: toont altijd het volledige `N: K1 / K2` formaat (de speler
   *   heeft de poort al opgelost, alle info mag onthuld worden).
   * - andere event-soorten in `keynotes`-vraagweergave en in `memory-poort`-
   *   spelmodus: enkel `K1 / K2`, anders zou het rij-label het antwoord
   *   verraden bij fouten of skips terwijl de speler de poort nog moet
   *   identificeren.
   * - andere event-soorten in `numbers`/`memory-keynote`-weergave: klassieke
   *   `Poort N` (de poort zelf is dan al de prompt en geen geheim).
   */
  private historyGateLabel(gate: number, kind: HdGateGuessingHistoryEvent['kind']): string {
    const { translations } = this.gateInfoService.gateInfoIn(gate, this.game.currentLang());
    const hasKeynotes = translations.keynote1 !== '' || translations.keynote2 !== '';

    if (kind === 'correct' && hasKeynotes) {
      return `${gate}: ${translations.keynote1} / ${translations.keynote2}`;
    }

    if (hasKeynotes && this.shouldHideGateInHistoryLabel(gate)) {
      return `${translations.keynote1} / ${translations.keynote2}`;
    }

    return this.t('humanDesign.games.gateGuessing.historyGateLabel', { gate });
  }

  /**
   * Het poortnummer mag niet in het rij-label verschijnen voor niet-correct
   * events wanneer de speler de poort nog moet ontdekken: dit geldt voor
   * `keynotes` (per-poort vraagweergave) en voor `memory-poort` (waar de
   * cheat-sheet en prompt het nummer juist verbergen).
   */
  private shouldHideGateInHistoryLabel(gate: number): boolean {
    return this.game.questionModeForGate(gate) === 'keynotes' || this.game.mode() === 'memory-poort';
  }

  /**
   * Globale toggle die bepaalt of de volledige hint-blokken standaard zichtbaar
   * zijn onder elke correct geraden poort in de history. Per-rij overrides
   * (zie `_historyExpansionOverrides`) gaan voor op deze default.
   */
  protected readonly showAllHintsInHistoryByDefault = signal(false);

  /**
   * Expliciete override per history-rij-key: `true` = uitgeklapt, `false` =
   * dichtgeklapt. Ontbrekende keys volgen de globale default. Wordt gevuld door
   * een klik op een correcte history-entry en weer opgeruimd zodra de keuze
   * samenvalt met de huidige default.
   */
  private readonly _historyExpansionOverrides = signal<ReadonlyMap<string, boolean>>(new Map());

  /**
   * Hint-slots per poort voor weergave in de history. Gebruikt de stabiele
   * volgorde die tijdens het spel werd vastgelegd. Het `gateNumber`-slot
   * wordt weggelaten omdat het rij-label van een correct geraden poort al
   * `N: K1 / K2` toont — de poortnummer is dus al onthuld op rijniveau.
   */
  protected readonly historyHintSlotsByGate = computed<ReadonlyMap<number, readonly HdGateGuessingHintSlot[]>>(() => {
    const events = this.game.events();
    const gates = new Set<number>();
    for (const event of events) {
      const gate = gateOfEvent(event);
      if (gate != null) {
        gates.add(gate);
      }
    }

    const lang = this.game.currentLang();
    const result = new Map<number, readonly HdGateGuessingHintSlot[]>();
    for (const gate of gates) {
      const info = this.gateInfoService.gateInfoIn(gate, lang);
      if (info.translations.keynote1 === '') {
        continue;
      }
      const order = this.game.hintExtraNotesOrderForGate(gate);
      result.set(gate, buildHintSlots(gate, info, order, 'numbers', this.gateInfoService, lang));
    }
    return result;
  });

  /**
   * Reset de per-rij overrides zodra de eventslijst leegloopt (typisch bij
   * spelherstart). Voorkomt dat verouderde keys blijven hangen wanneer in een
   * volgende ronde toevallig dezelfde key zou ontstaan.
   */
  private readonly _resetHistoryExpansionOnRestartEffect = effect(() => {
    if (this.game.events().length === 0) {
      untracked(() => this._historyExpansionOverrides.set(new Map()));
    }
  });

  protected readonly topSlowestGates = computed<readonly HdGateGuessingSlowestGate[]>(() => {
    const timings = [...this.game.completedGateTimings().entries()];
    return timings
      .sort(([, a], [, b]) => b - a)
      .slice(0, TOP_SLOWEST_GATES_COUNT)
      .map(([gate, elapsedMs]) => ({ gate, elapsedLabel: formatElapsedShort(elapsedMs) }));
  });

  protected readonly hasSlowGates = computed(() => this.slowGateThresholdMs() != null);

  protected readonly scoreLabel = computed(() => formatScore(this.game.score()));
  protected readonly totalScoreLabel = computed(() => formatScore(this.game.scoreWithTimeBonus()));
  protected readonly timeBonusLabelFormatted = computed(() => formatPoints(this.timeBonus()));

  protected readonly currentGateElapsedLabel = computed(() => formatElapsedShort(this.game.currentGateElapsedMs()));
  protected readonly totalElapsedLabel = computed(() => formatElapsedLong(this.game.totalElapsedMs()));

  protected readonly finishedTitle = computed(() => {
    const level = this.scoreLevel();
    return this.t(level ? SCORE_LEVEL_TITLE_KEYS[level] : 'humanDesign.games.gateGuessing.finishedFallbackTitle');
  });

  protected readonly starIndices = computed(() => indicesArray(this.scoreLevel() ?? 0));
  protected readonly emptyStarIndices = computed(() => indicesArray(5 - (this.scoreLevel() ?? 0)));

  protected requestHint(): void {
    if (this.mode() === 'memory-keynote') {
      this.requestMemoryHint();
      return;
    }
    this.game.requestHint();
    if (this.mode() === 'memory-poort') {
      this.focusMemoryGateInput();
    }
  }

  /**
   * Verlegt — indien beschikbaar — de focus terug naar het poortnummer-veld
   * van de memory-poort-modus. Gebruikt {@link setTimeout} met delay 0 om te
   * wachten tot Angular eventueel uitgesloten/her-renderde inputs in de DOM
   * geplaatst heeft (bv. na een mode-wissel die het veld pas conditioneel
   * mounten). No-op wanneer het veld niet aanwezig is.
   */
  private focusMemoryGateInput(): void {
    setTimeout(() => {
      const el = this.memoryGateInputEl()?.nativeElement;
      if (el == null) {
        return;
      }
      el.focus();
      try {
        const len = el.value.length;
        el.setSelectionRange(len, len);
      } catch {
        // setSelectionRange wordt niet door alle input-types ondersteund;
        // de focus zelf is voldoende voor onze use-case.
      }
    }, 0);
  }

  /**
   * Verlegt — indien beschikbaar — de focus naar het keynote1-veld van de
   * memory-keynote-modus. Zelfde delay-patroon als
   * {@link focusMemoryGateInput} zodat de input zeker in de DOM staat na een
   * mode-wissel of gate-wissel.
   */
  private focusMemoryKeynote1Input(): void {
    setTimeout(() => {
      const el = this.memoryKeynote1InputEl()?.nativeElement;
      if (el == null) {
        return;
      }
      el.focus();
      try {
        const len = el.value.length;
        el.setSelectionRange(len, len);
      } catch {
        // setSelectionRange wordt niet door alle input-types ondersteund;
        // de focus zelf is voldoende voor onze use-case.
      }
    }, 0);
  }

  /**
   * Memory-modus hint: vraagt de service om enkele letters te onthullen,
   * vervangt de keynote-invoer-signals door de teruggegeven waarden en wist
   * eventuele rode error-states (de onthulde tekst bevat de canonieke prefix
   * en is dus per definitie 'in orde'). Enkel relevant in `memory-keynote`;
   * `memory-poort` heeft geen hint-mechanisme.
   */
  private requestMemoryHint(): void {
    const reveal = this.game.requestMemoryHint(this.memoryKeynote1Input(), this.memoryKeynote2Input());
    if (reveal == null) {
      return;
    }
    this.memoryKeynote1Input.set(reveal.keynote1);
    this.memoryKeynote2Input.set(reveal.keynote2);
    this.memoryKeynote1Wrong.set(false);
    this.memoryKeynote2Wrong.set(false);
  }

  protected requestSkip(): void {
    this.game.skip();
    if (this.mode() === 'memory-poort') {
      this.focusMemoryGateInput();
    }
  }

  protected onMemoryGateInput(value: string): void {
    this.memoryGateInput.set(value);
    if (this.memoryGateWrong()) {
      this.memoryGateWrong.set(false);
    }
  }

  protected onMemoryKeynote1Input(value: string): void {
    this.memoryKeynote1Input.set(value);
    if (this.memoryKeynote1Wrong()) {
      this.memoryKeynote1Wrong.set(false);
    }
  }

  protected onMemoryKeynote2Input(value: string): void {
    this.memoryKeynote2Input.set(value);
    if (this.memoryKeynote2Wrong()) {
      this.memoryKeynote2Wrong.set(false);
    }
  }

  /**
   * Valideert losstaand één keynote-veld zonder score-impact. Bij succes
   * vervangen we de invoer door de canonieke spelling (zodat fuzzy-correcties
   * zichtbaar worden) en wissen de error-state. Bij fout antwoord kleurt het
   * veld rood tot de speler verder typt.
   */
  protected validateMemoryField(field: HdGateGuessingMemoryField): void {
    const typed = field === 'keynote1' ? this.memoryKeynote1Input() : this.memoryKeynote2Input();
    const result = this.game.validateMemoryField(field, typed);
    if (result.kind === 'correct') {
      if (field === 'keynote1') {
        this.memoryKeynote1Input.set(result.canonical);
        this.memoryKeynote1Wrong.set(false);
      } else if (field === 'keynote2') {
        this.memoryKeynote2Input.set(result.canonical);
        this.memoryKeynote2Wrong.set(false);
      }
      return;
    }
    if (result.kind === 'wrong') {
      if (field === 'keynote1') {
        this.memoryKeynote1Wrong.set(true);
      } else if (field === 'keynote2') {
        this.memoryKeynote2Wrong.set(true);
      }
    }
  }

  protected applyMemoryKeynote1Autocomplete(): void {
    const suggestion = this.memoryKeynote1AutocompleteSuggestion();
    if (suggestion != null) {
      this.memoryKeynote1Input.set(suggestion);
      this.memoryKeynote1Wrong.set(false);
    }
  }

  protected applyMemoryKeynote2Autocomplete(): void {
    const suggestion = this.memoryKeynote2AutocompleteSuggestion();
    if (suggestion != null) {
      this.memoryKeynote2Input.set(suggestion);
      this.memoryKeynote2Wrong.set(false);
    }
  }

  protected submitMemoryAnswer(): void {
    this.applyMemoryAnswer();
  }

  /**
   * Vult het poortnummer-veld in op basis van een chart-klik en submit
   * meteen — equivalent aan handmatig typen + Enter. Enkel actief in
   * `memory-poort`; in andere modi geeft deze methode `'ignored'` terug
   * zodat de orchestrator weet dat ze de standaard klik-flow moet behouden.
   * Op `'wrong'` wordt het poortnummer-veld leeggemaakt met de rode
   * error-stijl, net zoals bij een handmatig foutief antwoord.
   */
  submitMemoryGateFromChartClick(gate: number): HdGateGuessingMemoryAnswerResult['kind'] {
    if (this.mode() !== 'memory-poort') {
      return 'ignored';
    }
    this.memoryGateInput.set(String(gate));
    return this.applyMemoryAnswer().kind;
  }

  private applyMemoryAnswer(): HdGateGuessingMemoryAnswerResult {
    const previousGate = this.currentGate();
    const result = this.game.submitMemoryAnswer({
      gate: this.memoryGateInput(),
      keynote1: this.memoryKeynote1Input(),
      keynote2: this.memoryKeynote2Input(),
    });
    if (result.kind === 'wrong') {
      this.memoryGateWrong.set(result.gateWrong);
      this.memoryKeynote1Wrong.set(result.keynote1Wrong);
      this.memoryKeynote2Wrong.set(result.keynote2Wrong);
      if (result.gateWrong) {
        this.memoryGateInput.set('');
      }
      this.startMemoryWrongAnimation();
      this.feedbackAudio.playWrong();
    } else if (result.kind === 'correct') {
      this.clearMemoryInputs();
      if (previousGate != null) {
        this.startMemoryCelebration(previousGate);
      }
      this.feedbackAudio.playCorrect();
    }
    if (this.mode() === 'memory-poort') {
      this.focusMemoryGateInput();
    }
    return result;
  }

  /**
   * Tegenhanger van {@link startMemoryCelebration} voor een fout antwoord:
   * toont kort een rode cirkel met wit kruisje als overlay op de cheat-sheet
   * om de speler visueel feedback te geven dat het antwoord fout was. De
   * kaart blijft op dezelfde poort staan zodat de speler verder kan proberen.
   */
  private startMemoryWrongAnimation(): void {
    if (this.wrongAnimationTimerId != null) {
      clearTimeout(this.wrongAnimationTimerId);
      this.wrongAnimationTimerId = null;
    }
    this.wrongAnimating.set(false);
    setTimeout(() => {
      this.wrongAnimating.set(true);
      this.wrongAnimationTimerId = setTimeout(() => {
        this.wrongAnimating.set(false);
        this.wrongAnimationTimerId = null;
      }, MEMORY_WRONG_ANIMATION_DURATION_MS);
    }, 0);
  }

  /**
   * Toont de zojuist correct beantwoorde poort op de cheat-sheet inclusief
   * het ontbrekende deel + een groene rand met checkmark-overlay als
   * celebratie. Na {@link MEMORY_CELEBRATION_DURATION_MS} valt de kaart
   * terug op de (intussen al door de service aangepaste) volgende vraag.
   */
  private startMemoryCelebration(gate: number): void {
    if (this.celebrationTimerId != null) {
      clearTimeout(this.celebrationTimerId);
      this.celebrationTimerId = null;
    }
    this.celebratingGate.set(gate);
    this.celebrationTimerId = setTimeout(() => {
      this.celebratingGate.set(null);
      this.celebrationTimerId = null;
    }, MEMORY_CELEBRATION_DURATION_MS);
  }

  private clearMemoryInputs(): void {
    this.memoryGateInput.set('');
    this.memoryKeynote1Input.set('');
    this.memoryKeynote2Input.set('');
    this.memoryGateWrong.set(false);
    this.memoryKeynote1Wrong.set(false);
    this.memoryKeynote2Wrong.set(false);
  }

  protected toggleTimer(): void {
    this.game.toggleTimer();
  }

  protected readonly audioEnabled = this.feedbackAudio.enabled;

  protected toggleAudio(): void {
    this.feedbackAudio.toggleEnabled();
  }

  protected toggleShowAllHintsInHistoryByDefault(): void {
    this.showAllHintsInHistoryByDefault.update(v => !v);
  }

  /**
   * Een rij is uitklapbaar zodra ze aan een poort hangt en de hint-slots voor
   * die poort beschikbaar zijn. We tonen het hintblok enkel onder correct
   * geraden poorten — andere event-soorten (wrong, hint, skip) blijven
   * compact zoals voorheen.
   */
  protected isHistoryRowExpandable(row: HdGateGuessingHistoryRow): boolean {
    return row.kind === 'correct'
      && row.gate != null
      && (this.historyHintSlotsByGate().get(row.gate)?.length ?? 0) > 0;
  }

  protected isHistoryRowExpanded(row: HdGateGuessingHistoryRow): boolean {
    if (!this.isHistoryRowExpandable(row)) {
      return false;
    }
    const override = this._historyExpansionOverrides().get(row.key);
    return override ?? this.showAllHintsInHistoryByDefault();
  }

  protected hintSlotsForHistoryRow(row: HdGateGuessingHistoryRow): readonly HdGateGuessingHintSlot[] {
    return row.gate != null ? (this.historyHintSlotsByGate().get(row.gate) ?? []) : [];
  }

  protected toggleHistoryRowExpansion(row: HdGateGuessingHistoryRow): void {
    if (!this.isHistoryRowExpandable(row)) {
      return;
    }
    const desired = !this.isHistoryRowExpanded(row);
    const next = new Map(this._historyExpansionOverrides());
    if (desired === this.showAllHintsInHistoryByDefault()) {
      next.delete(row.key);
    } else {
      next.set(row.key, desired);
    }
    this._historyExpansionOverrides.set(next);
  }

  protected onHistoryRowKeydown(event: KeyboardEvent, row: HdGateGuessingHistoryRow): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    this.toggleHistoryRowExpansion(row);
  }

  protected async requestRestart(): Promise<void> {
    if (!this.game.isActive() || this.isFinished()) {
      this.restartRequested.emit();
      return;
    }

    const confirmed = await this.confirmer.confirm(
      this.t('humanDesign.games.gateGuessing.restartConfirm'),
      { color: 'danger' },
    );

    if (confirmed) {
      this.restartRequested.emit();
    }
  }
}

function indicesArray(count: number): readonly number[] {
  return Array.from({ length: Math.max(0, count) }, (_, i) => i);
}

/**
 * Strip alle HTML-tags uit een string zodat de tekstuele inhoud van een
 * hint-slot (bv. `<strong>Hoofd</strong>`) doorzoekbaar is als platte tekst.
 */
function stripHtmlTags(html: string): string {
  return html.replace(/<[^>]*>/g, '');
}

/**
 * Bouwt de geordende lijst van hint-slots voor één poort. Slots worden enkel
 * toegevoegd als hun bron-data beschikbaar is (bv. `mainCircuit` ontbreekt
 * voor poorten zonder kanaal-aansluiting). Het `gateNumber`-slot is enkel
 * relevant in `keynotes`-vraagweergave: in `numbers`-modus is het nummer al
 * de prompt zelf en heeft het geen onthullingswaarde.
 */
function buildHintSlots(
  gate: number,
  info: HdGateInfo,
  extraNotesOrder: readonly number[],
  questionMode: 'numbers' | 'keynotes' | null,
  gateInfoService: HdGateInfoService,
  lang: string,
): readonly HdGateGuessingHintSlot[] {
  const slots: HdGateGuessingHintSlot[] = [];

  slots.push({
    kind: 'center',
    categoryLabel: 'humanDesign.games.gateGuessing.categoryCenter',
    valueHtml: escapeHtml(gateInfoService.centerLabelIn(info.center, lang)),
  });

  // Extra info komt vóór het hoofdcircuit: center + hoofdcircuit samen zou de
  // poort vaak al weggeven, terwijl extraNotes een tussenstap met meer
  // betekenis vormen voor het leerproces.
  for (const noteIndex of extraNotesOrder) {
    const note = info.translations.extraNotes[noteIndex];
    if (note) {
      slots.push({ kind: 'extraNote', categoryLabel: 'humanDesign.games.gateGuessing.categoryExtra', valueHtml: renderBoldMarkdown(note) });
    }
  }

  if (info.mainCircuit) {
    const circuitName = gateInfoService.circuitNameIn(info.mainCircuit.key, lang);
    const themeLabel = info.mainCircuit.theme ? gateInfoService.circuitThemeLabelIn(info.mainCircuit.theme, lang) : null;
    const label = themeLabel ? `${circuitName} \u2014 ${themeLabel}` : circuitName;
    slots.push({
      kind: 'mainCircuit',
      categoryLabel: 'humanDesign.games.gateGuessing.categoryMainCircuit',
      valueHtml: escapeHtml(label),
    });
  }

  for (const note of info.translations.physicalNotes) {
    slots.push({ kind: 'physicalNote', categoryLabel: 'humanDesign.games.gateGuessing.categoryPhysical', valueHtml: renderBoldMarkdown(note) });
  }

  if (questionMode === 'keynotes') {
    slots.push({ kind: 'gateNumber', categoryLabel: 'humanDesign.games.gateGuessing.categoryGate', valueHtml: String(gate) });
  }

  return slots;
}

function computeHintMaxLevel(
  info: HdGateInfo,
  questionMode: 'numbers' | 'keynotes' | null,
): number {
  let count = 1; // center is altijd beschikbaar via metadata
  if (info.mainCircuit) {
    count += 1;
  }
  count += info.translations.extraNotes.length;
  count += info.translations.physicalNotes.length;
  if (questionMode === 'keynotes') {
    count += 1;
  }
  return count;
}

/**
 * Escapet HTML-special characters om veilig in `[innerHTML]` te kunnen
 * bindeen. Wordt gebruikt voor labels die geen markdown bevatten.
 */
function escapeHtml(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Vervangt eenvoudige `**bold**`-markdown door `<strong>`-tags. Alle overige
 * tekens worden vooraf geëscaped om injecties uit translation-data te
 * voorkomen.
 */
function renderBoldMarkdown(input: string): string {
  return escapeHtml(input).replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
}

/**
 * Fisher-Yates shuffle van [0..count-1]. Wordt eenmalig per poort opgeroepen
 * om de onthullingsvolgorde van extraNotes te randomiseren.
 */
function randomPermutation(count: number): readonly number[] {
  const result = Array.from({ length: count }, (_, i) => i);
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

function formatScore(score: number): string {
  return Number.isInteger(score) ? score.toString() : score.toFixed(1);
}

function formatPoints(points: number): string {
  const sign = points > 0 ? '+' : '';
  const value = Number.isInteger(points) ? points.toString() : points.toFixed(1);
  return `${sign}${value}`;
}

function formatElapsedShort(elapsedMs: number): string {
  const seconds = Math.max(0, Math.round(elapsedMs / 1000));
  return `${seconds}s`;
}

function formatElapsedLong(elapsedMs: number): string {
  const totalSeconds = Math.max(0, Math.round(elapsedMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes === 0) {
    return `${seconds}s`;
  }
  return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
}

function gateOfEvent(event: HdGateGuessingHistoryEvent): number | null {
  return event.kind === 'correct' || event.kind === 'wrong' || event.kind === 'hint' || event.kind === 'skip'
    ? event.gate
    : null;
}

function computeGroupPosition(
  events: readonly HdGateGuessingHistoryEvent[],
  index: number,
): HdGateGuessingHistoryGroupPosition {
  const currentGate = gateOfEvent(events[index]);
  if (currentGate == null) {
    return 'single';
  }

  const previousMatches = index > 0 && gateOfEvent(events[index - 1]) === currentGate;
  const nextMatches = index < events.length - 1 && gateOfEvent(events[index + 1]) === currentGate;

  if (previousMatches && nextMatches) return 'middle';
  if (previousMatches) return 'end';
  if (nextMatches) return 'start';
  return 'single';
}

function buildHistoryRow(
  events: readonly HdGateGuessingHistoryEvent[],
  index: number,
  slowThresholdMs: number | null,
  gateLabelResolver: (gate: number, kind: HdGateGuessingHistoryEvent['kind']) => string,
  translate: (key: string, params?: Record<string, unknown>) => string,
): HdGateGuessingHistoryRow {
  const event = events[index];
  const groupPosition = computeGroupPosition(events, index);

  switch (event.kind) {
    case 'correct':
      return {
        key: `correct-${event.gate}-${index}`,
        kind: event.kind,
        label: gateLabelResolver(event.gate, event.kind),
        icon: 'check',
        iconClass: 'history-icon-correct',
        pointsLabel: formatPoints(event.points),
        pointsClass: 'history-points-positive',
        elapsedLabel: formatElapsedShort(event.elapsedMs),
        elapsedIsSlow: slowThresholdMs != null && event.elapsedMs > slowThresholdMs,
        groupPosition,
        gate: event.gate,
      };
    case 'wrong':
      return {
        key: `wrong-${event.gate}-${index}`,
        kind: event.kind,
        label: gateLabelResolver(event.gate, event.kind),
        icon: 'times',
        iconClass: 'history-icon-wrong',
        pointsLabel: formatPoints(event.points),
        pointsClass: 'history-points-negative',
        elapsedLabel: null,
        elapsedIsSlow: false,
        groupPosition,
        gate: event.gate,
      };
    case 'hint':
      return {
        key: `hint-${event.gate}-${index}`,
        kind: event.kind,
        label: gateLabelResolver(event.gate, event.kind),
        icon: 'lightbulb',
        iconClass: 'history-icon-hint',
        pointsLabel: formatPoints(event.points),
        pointsClass: 'history-points-negative',
        elapsedLabel: null,
        elapsedIsSlow: false,
        groupPosition,
        gate: event.gate,
      };
    case 'skip':
      return {
        key: `skip-${event.gate}-${index}`,
        kind: event.kind,
        label: gateLabelResolver(event.gate, event.kind),
        icon: 'forward',
        iconClass: 'history-icon-skip',
        pointsLabel: formatPoints(event.points),
        pointsClass: 'history-points-negative',
        elapsedLabel: null,
        elapsedIsSlow: false,
        groupPosition,
        gate: event.gate,
      };
    case 'streak-bonus':
      return {
        key: `streak-${event.streak}-${index}`,
        kind: event.kind,
        label: translate('humanDesign.games.gateGuessing.historyStreak', { streak: event.streak }),
        icon: 'fire',
        iconClass: 'history-icon-streak',
        pointsLabel: formatPoints(event.points),
        pointsClass: 'history-points-positive',
        elapsedLabel: null,
        elapsedIsSlow: false,
        groupPosition,
        gate: null,
      };
    case 'perfect-bonus':
      return {
        key: `perfect-${index}`,
        kind: event.kind,
        label: translate('humanDesign.games.gateGuessing.historyPerfect'),
        icon: 'trophy',
        iconClass: 'history-icon-perfect',
        pointsLabel: formatPoints(event.points),
        pointsClass: 'history-points-positive',
        elapsedLabel: null,
        elapsedIsSlow: false,
        groupPosition,
        gate: null,
      };
  }
}

/**
 * Bepaalt of een tijd uitspringt boven de rest via Tukey's IQR-methode
 * (Q3 + 1.5·IQR). Robuust tegen extreme waarden en werkt goed wanneer de
 * meeste poorten ongeveer evenveel tijd kosten en enkele duidelijk afwijken.
 * Geeft `null` terug bij te weinig metingen of nul-spreiding (geen outliers).
 */
function computeSlowGateThresholdMs(timings: readonly number[]): number | null {
  if (timings.length < SLOW_OUTLIER_MIN_SAMPLE_SIZE) {
    return null;
  }

  const sorted = [...timings].sort((a, b) => a - b);
  const q1 = quantile(sorted, 0.25);
  const q3 = quantile(sorted, 0.75);
  const iqr = q3 - q1;

  if (iqr <= 0) {
    return null;
  }

  return q3 + SLOW_OUTLIER_IQR_MULTIPLIER * iqr;
}

function quantile(sortedValues: readonly number[], probability: number): number {
  const position = (sortedValues.length - 1) * probability;
  const lowerIndex = Math.floor(position);
  const upperIndex = Math.ceil(position);
  const fraction = position - lowerIndex;

  if (lowerIndex === upperIndex) {
    return sortedValues[lowerIndex];
  }

  return sortedValues[lowerIndex] + (sortedValues[upperIndex] - sortedValues[lowerIndex]) * fraction;
}
